// ============================================================
// EXTENSION SERVICE — Quản lý dữ liệu Đề nghị gia hạn
// Kết nối trực tiếp API Backend + Fallback thông minh
// ============================================================

import { REPORT_API } from '../apis/report';

let _cache = [];
const _subscribers = new Set();

const notifySubscribers = () => {
  _subscribers.forEach((cb) => {
    try {
      cb(_cache);
    } catch (e) {
      console.error('Error in extension subscriber:', e);
    }
  });
};

export const ExtensionService = {
  // Subscribe to changes
  subscribe(callback) {
    if (typeof callback === 'function') {
      _subscribers.add(callback);
      if (_cache.length > 0) {
        callback(_cache);
      }
    }
    return () => {
      _subscribers.delete(callback);
    };
  },

  // Get cached extensions by complaint ID or code
  getByComplaintId(complaintId, maPhanAnh) {
    if (!complaintId && !maPhanAnh) return [];
    return _cache.filter((item) => {
      const matchId =
        complaintId &&
        (item.id_phan_anh === complaintId ||
          item.complaintId === complaintId ||
          item.ma_phan_anh === complaintId ||
          item.phan_anh?.id === complaintId ||
          item.phan_anh?.ma_phan_anh === complaintId);
      const matchCode =
        maPhanAnh &&
        (item.ma_phan_anh === maPhanAnh ||
          item.id_phan_anh === maPhanAnh ||
          item.complaintId === maPhanAnh ||
          item.phan_anh?.ma_phan_anh === maPhanAnh ||
          item.phan_anh?.id === maPhanAnh);
      return Boolean(matchId || matchCode);
    });
  },

  // Get all extensions with query filters
  async getAll(params = {}) {
    try {
      const res = await REPORT_API.getExtensions({ size: 100, ...params });
      const data = res?.data || [];
      if (Array.isArray(data)) {
        _cache = data;
        notifySubscribers();
      }
      return data;
    } catch (err) {
      console.error('Error fetching extensions from API:', err);
      return _cache;
    }
  },

  // Get extension statistics counts (all, pending, approved, rejected)
  async getStats(params = {}) {
    try {
      const data = await REPORT_API.getExtensionStats(params);
      return data || { all: 0, pending: 0, approved: 0, rejected: 0 };
    } catch (err) {
      console.error('Error fetching extension stats:', err);
      return { all: 0, pending: 0, approved: 0, rejected: 0 };
    }
  },

  // Get extension pagination
  async getPagination(params = {}) {
    try {
      const res = await REPORT_API.getExtensions(params);
      if (Array.isArray(res?.data)) {
        // Merge into cache
        const existingIds = new Set(_cache.map((x) => x.id));
        res.data.forEach((item) => {
          if (!existingIds.has(item.id)) {
            _cache.push(item);
          }
        });
        notifySubscribers();
      }
      return res;
    } catch (err) {
      console.error('Error fetching extensions pagination:', err);
      return { data: [], pagination: { totalItems: 0, totalPages: 1 } };
    }
  },

  // Get extension by ID
  async getById(id) {
    try {
      return await REPORT_API.getExtensionById(id);
    } catch (err) {
      console.error('Error fetching extension detail:', err);
      throw err;
    }
  },

  // Create a new extension request
  async create({ complaintId, requestedDeadline, reason, files = [] }) {
    try {
      const result = await REPORT_API.createExtension({
        complaintId,
        requestedDeadline,
        reason,
        files,
      });
      this.getAll().catch(() => {});
      return result;
    } catch (err) {
      console.error('Error creating extension:', err);
      throw err;
    }
  },

  // Leader approves extension request
  async approve(id, note = '') {
    try {
      const result = await REPORT_API.approveExtension(id, note);
      this.getAll().catch(() => {});
      return result;
    } catch (err) {
      console.error('Error approving extension:', err);
      throw err;
    }
  },

  // Leader rejects extension request
  async reject(id, note = '') {
    try {
      const result = await REPORT_API.rejectExtension(id, note);
      this.getAll().catch(() => {});
      return result;
    } catch (err) {
      console.error('Error rejecting extension:', err);
      throw err;
    }
  },

  // Export extensions to Excel via Backend API
  async exportExcel(params = {}) {
    try {
      return await REPORT_API.exportExtensionExcel(params);
    } catch (err) {
      console.error('Error exporting extensions to Excel:', err);
      return { success: false, error: err?.message || 'Lỗi xuất file Excel' };
    }
  },
};
